import React from 'react';

/**
 * Error boundary around every Qualifying Agent surface (the candidates-table cell, the replay
 * modal, the screening-details card).
 *
 * Rationale: React unmounts the whole tree when a render throws, so without this a single bad
 * qualifying session — a malformed transcript turn, a missing field after a schema change —
 * would blank out the entire candidates table and take every unrelated feature with it. The
 * qualifying stage is newer and changes more often than what surrounds it, so it is the part
 * most likely to throw and the part that least deserves to break everything else.
 *
 * Failures degrade to an inert marker and are reported to the console with a
 * `[QualifyingAgent]` prefix, so the origin is unambiguous to whoever picks up the bug.
 */
export default class QualifyingAgentBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error(
      `[QualifyingAgent] UI error in "${this.props.surface || 'unknown surface'}" — this is contained and has not affected the rest of the page.`,
      error,
      info?.componentStack
    );
  }

  render() {
    if (this.state.failed) {
      if (this.props.fallback !== undefined) return this.props.fallback;
      return (
        <span className="text-xs text-gray-400" title="Qualifying agent data could not be displayed">
          Unavailable
        </span>
      );
    }
    return this.props.children;
  }
}
